/**
 * Promotions v2 data layer. Reads the typed board from /api/promo-board and
 * reshapes it into per-assortment "bands" (range entities → calendar spans) for
 * the Sales grid, per-channel lanes for the Marketing Gantt, a date→week map for
 * the KeySellDates overlay, plus all the typed mutations + the publish/snapshot
 * workflow. Mirrors the optimistic/invalidate patterns from usePromoGrid.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';

const QK = (fy) => ['promo-board', fy];

// Typed mechanic → display string (the inverse of the migration parser).
export function promoLabel(p) {
  switch (p.mechanicType) {
    case 'PercentOff': return `${Math.round(p.discountPct ?? 0)}% OFF${p.notes ? ` ${p.notes}` : ''}`;
    case 'MultiBuy': return `${p.qty} FOR $${p.pricePoint}`;
    case 'EDLP': return `$${p.pricePoint}${p.notes ? ` ${p.notes}` : ' LOW PRICE'}`;
    case 'SinglePrice': return `${p.pricePoint}`;
    default: return p.mechanicType;
  }
}

function reshape(raw) {
  if (!raw) return null;
  const { assortments = [], promotions = [], media = [], activations = [], marketing = [],
          events = [], calendar = [], keySellDates = [], accounts = [] } = raw;

  const byAsst = new Map();
  const ensure = (id) => {
    let o = byAsst.get(id);
    if (!o) { o = { price: [], scan: [], media: [], activation: [] }; byAsst.set(id, o); }
    return o;
  };
  for (const p of promotions) {
    ensure(p.assortmentId).price.push({ kind: 'promotion', id: p.promotionId, rowVersion: p.rowVersion,
      startWeek: p.startWeek, endWeek: p.endWeek, label: promoLabel(p), status: p.status, raw: p });
    if (p.scanDealPerUnit != null) ensure(p.assortmentId).scan.push({ kind: 'scan', id: p.promotionId,
      startWeek: p.startWeek, endWeek: p.endWeek, label: String(p.scanDealPerUnit), status: p.status, raw: p });
  }
  for (const m of media) ensure(m.assortmentId).media.push({ kind: 'media', id: m.mediaId,
    startWeek: m.startWeek, endWeek: m.endWeek, label: m.mediaType + (m.note ? ` · ${m.note}` : ''), status: m.status, raw: m });
  for (const a of activations) ensure(a.assortmentId).activation.push({ kind: 'activation', id: a.activationId,
    startWeek: a.startWeek, endWeek: a.endWeek, label: [a.code, a.storeCount ? `(${a.storeCount})` : '', a.displayType].filter(Boolean).join(' '), status: a.status, raw: a });

  // Marketing lanes: group by Channel → brand sub-lanes; bands per campaign.
  const mktLanes = new Map();
  for (const c of marketing) {
    if (!mktLanes.has(c.channel)) mktLanes.set(c.channel, []);
    mktLanes.get(c.channel).push({ kind: 'campaign', id: c.campaignId, rowVersion: c.rowVersion,
      startWeek: c.startWeek, endWeek: c.endWeek,
      label: `${c.campaignName}${c.brand ? ` · ${c.brand}` : ''}${c.budget != null ? ` · $${Number(c.budget).toLocaleString()}` : ''}`,
      status: c.status, raw: c });
  }
  const marketingLanes = [...mktLanes.entries()].map(([channel, bands]) => ({ channel, bands }));

  // date(yyyy-mm-dd) → fiscal week, via the calendar.
  const dateToWeek = (d) => {
    if (!d) return null;
    const row = calendar.find((c) => d >= c.startDate && d <= c.endDate);
    return row ? row.week : null;
  };
  // KeySellDates → week-keyed milestone markers.
  const milestones = {}; // week → [{customer, kind}]
  const addM = (wk, customer, kind) => { if (wk) (milestones[wk] ||= []).push({ customer, kind }); };
  for (const k of keySellDates) {
    addM(dateToWeek(k.decisionDate), k.customer, 'Decision');
    addM(dateToWeek(k.dcDate), k.customer, 'DC');
    addM(dateToWeek(k.pogDate), k.customer, 'POG');
    addM(dateToWeek(k.rangeReviewDate), k.customer, 'Range Review');
  }

  const totals = {
    drafts: promotions.filter((p) => p.status === 'draft').length
          + media.filter((m) => m.status === 'draft').length
          + activations.filter((a) => a.status === 'draft').length
          + marketing.filter((c) => c.status === 'draft').length,
    promotions: promotions.length,
    campaigns: marketing.length,
    budget: marketing.reduce((s, c) => s + (Number(c.budget) || 0), 0),
  };

  return {
    accounts, assortments, byAsst, marketingLanes, events, calendar, milestones, totals,
    myRole: raw.myRole, raw,
  };
}

export function usePromoBoard(fy = 26) {
  const apiFetch = useApi();
  const query = useQuery({
    queryKey: QK(fy),
    queryFn: async () => {
      const res = await apiFetch(`/promo-board?fy=${fy}`);
      if (!res.ok) throw new Error(`Failed to load board: ${res.status}`);
      return res.json();
    },
    retry: false,
  });
  const board = useMemo(() => reshape(query.data), [query.data]);
  return { board, isLoading: query.isLoading, error: query.error, isFetching: query.isFetching };
}

export function usePromoBoardMutations(fy = 26) {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: QK(fy) });

  // Two top-level mutations + thin dispatch helpers (avoids calling hooks in a
  // loop/closure). `base` selects the endpoint; rowVersion → If-Match.
  const saveMut = useMutation({
    mutationFn: async ({ base, id, rowVersion, payload }) => {
      const method = id ? 'PUT' : 'POST';
      const path = id ? `/${base}/${id}` : `/${base}`;
      const headers = rowVersion ? { 'If-Match': rowVersion } : undefined;
      const res = await apiFetch(path, { method, headers, body: JSON.stringify({ fy, ...payload }) });
      if (!res.ok) { const t = await res.text().catch(() => ''); throw new Error(`HTTP ${res.status} ${t.slice(0, 180)}`); }
      return res.json();
    },
    onSettled: invalidate,
  });
  const removeMut = useMutation({
    mutationFn: async ({ base, id }) => {
      const res = await apiFetch(`/${base}/${id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      return true;
    },
    onSettled: invalidate,
  });
  const publish = useMutation({
    mutationFn: async () => {
      const res = await apiFetch('/plan-publish', { method: 'POST', body: JSON.stringify({ fy }) });
      if (!res.ok) throw new Error(`Publish failed: ${res.status}`);
      return res.json();
    },
    onSettled: invalidate,
  });
  const snapshot = useMutation({
    mutationFn: async (label) => {
      const res = await apiFetch('/plan-snapshot', { method: 'POST', body: JSON.stringify({ fy, label }) });
      if (!res.ok) throw new Error(`Snapshot failed: ${res.status}`);
      return res.json();
    },
  });

  return {
    saveMut, removeMut, publish, snapshot,
    save: (base, { id, rowVersion, ...payload }, opts) => saveMut.mutate({ base, id, rowVersion, payload }, opts),
    remove: (base, id, opts) => removeMut.mutate({ base, id }, opts),
  };
}
